import { mapBackendToJSX } from '../utils/mappings';

const QUEUE_KEY = 'aquareport.offlineQueue.v1';

export const isOfflineError = (error) => {
  if (!navigator.onLine) return true;
  if (!error.response) return true;
  return false;
};

const readQueue = () => {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const writeQueue = (items) => {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(items));
  window.dispatchEvent(new CustomEvent('aquareport:offline-queue-changed'));
};

export const getOfflineQueue = () => readQueue();

export const getPendingRegistroCount = () =>
  readQueue().filter(item => item.type === 'registro:create').length;

export const queueRegistroCreate = (payload, metadata = {}) => {
  const item = {
    id: `offline-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    type: 'registro:create',
    payload,
    metadata,
    createdAt: new Date().toISOString(),
    attempts: 0,
  };

  writeQueue([...readQueue(), item]);
  return item;
};

// Corrige un registro que el propio operario creó offline y todavía no se
// sincronizó: no hay id real en el servidor todavía, así que no tiene
// sentido (ni es posible) mandar un PUT — se reemplaza el payload de la
// misma entrada en la cola en vez de crear una segunda entrada "editar".
export const updateQueuedRegistro = (offlineId, payload, metadata = {}) => {
  const queue = readQueue();
  const idx = queue.findIndex(item => item.id === offlineId && item.type === 'registro:create');
  if (idx === -1) return null;
  const next = [...queue];
  next[idx] = { ...next[idx], payload, metadata: { ...next[idx].metadata, ...metadata } };
  writeQueue(next);
  return next[idx];
};

export const getPendingRegistrosAsJSX = () =>
  readQueue()
    .filter(item => item.type === 'registro:create')
    .map(item => ({
      ...mapBackendToJSX({
        ...item.payload,
        id: item.id,
        encargado_nombre: item.metadata.operarioNombre || item.payload.encargado,
      }),
      id: item.id,
      offlinePending: true,
      offlineCreatedAt: item.createdAt,
    }));

// ── Lavado de Filtros — mismo mecanismo que Registros: si falla por falta
// de conexión, se guarda en la cola y se sincroniza solo al reconectar.
export const getPendingLavadoCount = () =>
  readQueue().filter(item => item.type === 'lavado:create').length;

export const queueLavadoCreate = (payload, metadata = {}) => {
  const item = {
    id: `offline-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    type: 'lavado:create',
    payload,
    metadata,
    createdAt: new Date().toISOString(),
    attempts: 0,
  };

  writeQueue([...readQueue(), item]);
  return item;
};

// Duración/volumen del lavado calculados en el cliente para mostrarlos en la
// lista mientras está pendiente — el backend (LavadoFiltro.volumen_m3) hace
// exactamente la misma cuenta una vez se sincroniza, así que el número no
// debería "saltar" cuando el registro deje de estar pendiente.
const _duracionSegundosLavado = (fecha, horaInicio, horaFin) => {
  const inicio = new Date(`${fecha}T${horaInicio}`);
  let fin = new Date(`${fecha}T${horaFin}`);
  if (fin < inicio) fin = new Date(fin.getTime() + 24 * 3600 * 1000); // cruza medianoche
  return (fin - inicio) / 1000;
};

export const getPendingLavadosAsJSX = () =>
  readQueue()
    .filter(item => item.type === 'lavado:create')
    .map(item => {
      const { fecha, filtro, hora_inicio, hora_fin, caudal_ls } = item.payload;
      const segundos = _duracionSegundosLavado(fecha, hora_inicio, hora_fin);
      return {
        id: item.id,
        fecha, filtro, hora_inicio, hora_fin, caudal_ls,
        duracion_minutos: Math.round((segundos / 60) * 10) / 10,
        volumen_m3: Math.round((caudal_ls * segundos / 1000) * 100) / 100,
        registrado_por_nombre: item.metadata.operarioNombre || '',
        offlinePending: true,
        offlineCreatedAt: item.createdAt,
      };
    });

export const syncOfflineQueue = async ({ registroDiarioAPI, lavadoFiltroAPI, onProgress } = {}) => {
  const queue = readQueue();
  if (!queue.length || !navigator.onLine) {
    return { synced: 0, failed: 0, remaining: queue.length };
  }

  const remaining = [];
  let synced = 0;
  let failed = 0;

  const ejecutar = (item) => {
    if (item.type === 'registro:create' && registroDiarioAPI) return registroDiarioAPI.create(item.payload);
    if (item.type === 'lavado:create' && lavadoFiltroAPI) return lavadoFiltroAPI.create(item.payload);
    return null;
  };

  for (let index = 0; index < queue.length; index += 1) {
    const item = queue[index];
    const peticion = ejecutar(item);

    if (!peticion) {
      remaining.push(item); // tipo desconocido, o no se pasó el cliente API correspondiente
      continue;
    }

    try {
      await peticion;
      synced += 1;
      if (onProgress) onProgress(item);
    } catch (error) {
      failed += 1;
      remaining.push({
        ...item,
        attempts: (item.attempts || 0) + 1,
        lastError: error.response?.data || error.message || 'sync_error',
      });

      if (isOfflineError(error)) {
        remaining.push(...queue.slice(index + 1));
        break;
      }
    }
  }

  writeQueue(remaining);
  return { synced, failed, remaining: remaining.length };
};
